import { Link } from "wouter";
import { Briefcase, Clock, Gavel, MapPin, Wrench } from "lucide-react";
import { CoverArt } from "./cards";
import { CompanyMediaPreview } from "./CompanyMediaPreview";
import { formatCompactCardPhp, formatPhp } from "../lib/money";
import { useI18n } from "../lib/i18n";

export type OwnedService = {
  id: string;
  ownerUserId?: number | null;
  title: string;
  by: string;
  city: string;
  fromPrice: number;
  hidden: boolean;
  ownerClosed?: boolean;
  moderationHidden?: boolean;
  status: "open" | "closed" | "archived";
  coverImage?: string | null;
};

export type OwnedJob = {
  id: string;
  ownerUserId?: number | null;
  ownerClosed?: boolean;
  moderationHidden?: boolean;
  title: string;
  company: string;
  city: string;
  type: string | null;
  applicants: number;
  hidden: boolean;
  status: string;
  coverImage?: string | null;
};

export type MemberCompany = {
  verified?: boolean;
  verificationStatus?: "verified" | "pending" | "rejected" | "not_submitted";
  id: string;
  ownerUserId?: number | null;
  ownerClosed?: boolean;
  moderationHidden?: boolean;
  name: string;
  tagline: string | null;
  city: string;
  initials: string;
  role: string;
  hidden: boolean;
};

export type SubmittedBid = {
  id: string;
  projectId: string;
  projectTitle: string | null;
  status: string;
  price: number;
  finalPrice: number | null;
  durationText: string | null;
  createdAt: string;
  coverImage?: string | null;
};

import { type JobOffer } from "../lib/api";
import { jobOfferHref, jobOfferStatus, monthlySalaryText } from "./JobOffer";
import { EmploymentLink } from "./EmploymentLink";

export type MemberApplication = {
  id: string;
  jobId: string;
  jobTitle: string | null;
  jobCity: string | null;
  jobStatus: string | null;
  status: string;
  createdAt: string;
  interviewAt: string | null;
  interviewNote: string | null;
  jobOffer?: JobOffer | null;
  agreedSalary?: number | null;
  coverImage?: string | null;
};

const cardClass = "card group flex h-full min-w-0 flex-col overflow-hidden transition-all duration-150 hover:-translate-y-0.5 hover:border-navy/30 hover:shadow-lift focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red";
const statusColors: Record<string, string> = {
  Live: "bg-brand-green/10 text-brand-greendark",
  Archived: "bg-navy-50 text-mut",
  Closed: "bg-navy-50 text-mut",
  "Under review": "bg-blue-50 text-blue-700",
  Shortlisted: "bg-blue-50 text-blue-700",
  "In negotiation": "bg-amber-100 text-amber-700",
  Awarded: "bg-brand-gold/15 text-[#8a5a00]",
  Rejected: "bg-red-100 text-brand-red",
  Applied: "bg-navy-50 text-navy",
  Accepted: "bg-emerald-100 text-emerald-700",
  Withdrawn: "bg-navy-50 text-mut",
};

function Status({ label, displayLabel = label }: { label: string; displayLabel?: string }) {
  return <span className={`chip shrink-0 px-1.5 py-1 text-[8px] sm:px-2.5 sm:text-xs ${statusColors[label] || "bg-navy-50 text-navy"}`}>{displayLabel}</span>;
}

function Location({ city, detail }: { city: string | null; detail?: string | null }) {
  return (
    <p className="flex min-w-0 items-center gap-1 text-[10px] text-mut sm:text-xs">
      <MapPin className="h-3 w-3 shrink-0 text-brand-red sm:h-3.5 sm:w-3.5" />
      <span className="truncate">{city || "Philippines"}</span>
      {detail && <span className="shrink-0">| {detail}</span>}
    </p>
  );
}

function formatDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "Date unavailable" : date.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" });
}

export function OwnedServiceCard({ service }: { service: OwnedService }) {
  const { t } = useI18n();
  const label = service.status === "archived" ? "Archived" : service.status === "closed" ? "Closed" : service.hidden ? "Hidden" : "Live";
  const statusKey = service.status === "archived" ? "archivedLabel" : service.status === "closed" ? "closed" : service.hidden ? "hidden" : "live";
  const content = (
    <>
      <div className="aspect-[4/3] overflow-hidden bg-navy-50 sm:aspect-square"><CoverArt src={service.coverImage} alt="" Icon={Wrench} /></div>
      <div className="flex flex-1 flex-col gap-2 p-2.5 sm:gap-2.5 sm:p-3.5">
        <div className="flex min-w-0 items-start justify-between gap-1 sm:items-center sm:gap-2">
          <h2 className="min-w-0 line-clamp-2 text-[11px] font-bold leading-snug text-navy group-hover:text-brand-red sm:line-clamp-1 sm:text-sm" title={service.title}>{service.title}</h2>
          <Status label={label} displayLabel={t(`dashboard.status.${statusKey}`, label)} />
        </div>
        <p className="truncate text-[10px] text-mut sm:text-xs">by {service.by}</p>
        <Location city={service.city} />
        <div className="mt-auto flex items-end justify-between gap-1 border-t border-line pt-2 sm:gap-2 sm:pt-2.5">
          <span className="min-w-0 text-[9px] text-mut sm:text-xs">Starting at</span>
          <span className="shrink-0 whitespace-nowrap text-right text-[11px] font-bold text-navy sm:text-sm">{service.fromPrice > 0 ? <><span className="sm:hidden">{formatCompactCardPhp(service.fromPrice)}</span><span className="hidden sm:inline">{formatPhp(service.fromPrice)}</span></> : "Not provided"}</span>
        </div>
      </div>
    </>
  );
  return <Link href={`/s/${service.id}`} className={cardClass} aria-label={`Open ${service.title}`}>{content}</Link>;
}

export function MemberCompanyCard({ company }: { company: MemberCompany }) {
  const canManage = company.role === "owner";
  return (
    <article className="card group h-full overflow-hidden transition-all duration-150 hover:-translate-y-0.5 hover:border-navy/30 hover:shadow-lift">
      <Link href={company.verified ? `/c/${company.id}` : `/company/${company.id}/dashboard`} className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-red" aria-label={`Open ${company.name}`}>
        <div className="h-[58px] bg-[#1a3e60] sm:h-[135px]" />
        <div className="relative z-[1] mx-auto -mt-8 grid h-16 w-16 place-items-center overflow-hidden rounded-full border-[3px] border-white bg-navy-50 text-xl font-bold text-navy shadow-lg sm:-mt-[67px] sm:h-[134px] sm:w-[134px] sm:border-4 sm:text-4xl">
          <CompanyMediaPreview companyId={company.id} kind="logo" alt="" className="h-full w-full object-contain" fallback={company.initials} />
        </div>
        <div className="flex min-w-0 flex-col items-center gap-1 px-2 pt-2 text-center sm:px-4 sm:pt-3">
          <h2 className="max-w-full line-clamp-2 text-[11px] font-bold text-navy group-hover:text-brand-red sm:line-clamp-1 sm:text-base" title={company.name}>{company.name}</h2>
          {!company.verified && <p className="text-xs text-mut">Private · {company.verificationStatus === "pending" ? "Under review" : company.verificationStatus === "rejected" ? "Needs changes" : "Verification needed"}</p>}
          {company.tagline && <p className="max-w-full truncate text-[10px] text-mut sm:text-sm" title={company.tagline}>{company.tagline}</p>}
          <div className="mt-2 sm:mt-3"><Location city={company.city} /></div>
        </div>
      </Link>
      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 px-2 pb-3 pt-2 text-[10px] text-mut sm:px-3 sm:pb-5 sm:pt-4 sm:text-xs">
        <span className="capitalize">{company.role}</span>
        {company.hidden && <><span>·</span><Status label={company.ownerClosed ? "Closed" : "Hidden"} /></>}
        {canManage && <><span>·</span><Link href={`/company/${company.id}/dashboard`} className="font-semibold text-navy hover:text-brand-red focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-red">Dashboard</Link></>}
      </div>
    </article>
  );
}

export function OwnedJobCard({ job }: { job: OwnedJob }) {
  const status = job.status === "closed" ? "Closed" : job.hidden ? "Archived" : "Live";
  return (
    <Link href={`/j/${job.id}`} className={`${cardClass} gap-2 p-2.5 sm:gap-3 sm:p-4`} aria-label={`Open ${job.title}`}>
      <div className="-mx-2.5 -mt-2.5 aspect-[16/10] overflow-hidden bg-navy-50 sm:-mx-4 sm:-mt-4 sm:aspect-[4/3]"><CoverArt src={job.coverImage} alt="" Icon={Briefcase} /></div>
      <div className="flex min-w-0 items-start gap-3">
        <div className="hidden h-11 w-11 shrink-0 place-items-center rounded-lg bg-navy-50 text-navy sm:grid"><Briefcase className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <h2 className="line-clamp-2 text-[11px] font-bold leading-snug text-navy group-hover:text-brand-red sm:line-clamp-1 sm:text-sm" title={job.title}>{job.title}</h2>
          <p className="mt-1 truncate text-[10px] text-mut sm:text-xs" title={job.company}>{job.company}</p>
          <div className="mt-1"><Location city={job.city} detail={job.type} /></div>
        </div>
      </div>
      <div className="mt-auto flex flex-wrap items-end justify-between gap-1 border-t border-line pt-2 sm:gap-2 sm:pt-2.5">
        <span className="text-[11px] font-bold text-navy sm:text-sm">{job.applicants} {job.applicants === 1 ? "applicant" : "applicants"}</span>
        <Status label={status} />
      </div>
    </Link>
  );
}

export function SubmittedBidCard({ bid }: { bid: SubmittedBid }) {
  const { t } = useI18n();
  const status = ({ submitted: "Under review", shortlisted: "Shortlisted", in_negotiation: "In negotiation", awarded: "Awarded", rejected: "Rejected" } as Record<string, string>)[bid.status] || bid.status;
  const displayStatus = bid.status === "shortlisted" ? t("activity.status.shortlisted")
    : bid.status === "awarded" ? t("activity.status.awarded") : status;
  const amount = bid.status === "awarded" && bid.finalPrice != null ? bid.finalPrice : bid.price;
  return (
    <Link href={`/project/${bid.projectId}`} className={`${cardClass} gap-2 p-2.5 sm:gap-2.5 sm:p-4`} aria-label={`Open ${bid.projectTitle || "project"}`}>
      <div className="-mx-2.5 -mt-2.5 aspect-[16/10] overflow-hidden bg-navy-50 sm:-mx-4 sm:-mt-4 sm:aspect-[4/3]"><CoverArt src={bid.coverImage} alt="" Icon={Gavel} /></div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="chip bg-brand-gold/15 px-1.5 text-[8px] text-[#8a5a00] sm:px-2.5 sm:text-xs">Project Bid</span>
        <Status label={status} displayLabel={displayStatus} />
      </div>
      <h2 className="line-clamp-2 text-[11px] font-bold leading-snug text-navy group-hover:text-brand-red sm:line-clamp-1 sm:text-sm" title={bid.projectTitle || "Project"}>{bid.projectTitle || "Project"}</h2>
      <Location city="Philippines" />
      <p className="text-[10px] text-mut sm:text-xs">{t("bid.submitted")} {formatDate(bid.createdAt)}</p>
      <div className="mt-auto flex items-end justify-between gap-1 border-t border-line pt-2 sm:gap-2 sm:pt-2.5">
        <span className="min-w-0 text-[9px] text-mut sm:text-xs">Your bid</span>
        <span className="shrink-0 whitespace-nowrap text-right text-[11px] font-bold text-navy sm:text-sm"><span className="sm:hidden">{formatCompactCardPhp(amount)}</span><span className="hidden sm:inline">{formatPhp(amount)}</span></span>
      </div>
    </Link>
  );
}

export function MemberApplicationCard({ application }: { application: MemberApplication }) {
  const { t } = useI18n();
  const status = application.status.charAt(0).toUpperCase() + application.status.slice(1);
  const displayStatus = application.status === "shortlisted" ? t("activity.status.shortlisted") : application.status === "accepted" ? t("hiring.mobile.accepted") : status;
  return (
    <article className={`${cardClass} gap-2 p-2.5 sm:gap-3 sm:p-4`}><Link href={application.jobOffer ? jobOfferHref(application.jobOffer) : `/j/${application.jobId}`} className="flex min-w-0 flex-1 flex-col gap-2 sm:gap-3" aria-label={`Open ${application.jobTitle || "job"}`}>
      <div className="-mx-2.5 -mt-2.5 aspect-[16/10] overflow-hidden bg-navy-50 sm:-mx-4 sm:-mt-4 sm:aspect-[4/3]"><CoverArt src={application.coverImage} alt="" Icon={Briefcase} /></div>
      <div className="flex min-w-0 items-start gap-3">
        <div className="hidden h-11 w-11 shrink-0 place-items-center rounded-lg bg-navy-50 text-navy sm:grid"><Briefcase className="h-5 w-5" /></div>
        <div className="min-w-0 flex-1">
          <h2 className="line-clamp-2 text-[11px] font-bold leading-snug text-navy group-hover:text-brand-red sm:line-clamp-1 sm:text-sm" title={application.jobTitle || "Job"}>{application.jobTitle || "Job"}</h2>
          <p className="mt-1 truncate text-[10px] text-mut sm:text-xs">Job application</p>
          <div className="mt-1"><Location city={application.jobCity} detail={application.jobStatus === "closed" ? "Job closed" : null} /></div>
        </div>
      </div>
      {application.jobOffer && <p className="text-xs font-semibold text-navy">{jobOfferStatus(application.jobOffer)} · {monthlySalaryText(application.jobOffer.salary)}<span className="mt-1 block text-blue-700">{application.jobOffer.status === "sent" ? "Review offer" : "View offer history"} →</span></p>}
      {application.interviewAt && <p className="flex items-center gap-1 text-[10px] font-semibold text-[#8a5a00] sm:text-xs"><Clock className="h-3 w-3 shrink-0 sm:h-3.5 sm:w-3.5" />Interview: {new Date(application.interviewAt).toLocaleString("en-PH", { dateStyle: "medium", timeStyle: "short" })}{application.interviewNote && <span className="truncate font-normal text-mut">· {application.interviewNote}</span>}</p>}
      <div className="mt-auto flex flex-wrap items-end justify-between gap-1 border-t border-line pt-2 sm:gap-2 sm:pt-2.5">
        <span className="text-[9px] text-mut sm:text-xs">Applied {formatDate(application.createdAt)}</span>
        <Status label={status} displayLabel={displayStatus} />
      </div>
    </Link>{application.status === "accepted" && <div className="border-t border-line pt-2"><EmploymentLink applicationId={application.id} engagementId={application.jobOffer?.engagementId} /></div>}</article>
  );
}
